import { useCopilotRuntimeOptional } from '@/components/agent-chat/copilot-runtime-provider.component';
import { proposalRendered } from '@/components/agent-chat/play-draft.util';
import type { ReactNode } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useOrganization } from '@clerk/react';
import { useAgentsAccess } from '@/hooks/use-agents-access.hook';
import { stepHeadline } from '@/containers/playbooks/custom-builder/connectors';
import { graphTrigger } from '@/containers/playbooks/custom-builder/test-record.util';
import { useHeldRecordsDecision } from '@/containers/playbooks/custom-builder/use-held-records-decision.hook';
import {
  HoldDecisionCancelled,
  holdsBody,
} from '@/containers/playbooks/custom-builder/held-records';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { EmbeddedProposal } from '@/containers/playbooks/custom-builder/proposal';
import {
  useAcceptPlayProposalMutation,
  useDiscardPlayProposalMutation,
  useGetPlayProposalQuery,
} from '@/store/features/gtm-agents.feature';
import { pendingPlaySave } from '@/containers/playbooks/custom-builder/use-play-autosave.hook';
import {
  clearAgentPlayDraft,
  selectAgentPlayDrafts,
  type AgentPlayDraft,
} from '@/store/features/ui.feature';
import type { CustomGraph } from '@/types/custom-playbooks';
import type { AgentDto, PlayProposalStatus } from '@/types/gtm-agents';
import { abilitiesTurnedOn } from './agent-enforcement.util';
import { proposalIsBlocked } from './play-proposal-review.util';
import { isMissingRecord, serverReason } from './server-reason.util';
import { addedAgentPlay, selectedAgentPlay } from './agent-plays.util';
import { useAgentEditor } from './use-agent-editor.hook';
import { useRefreshAgentChat } from './use-refresh-agent-chat.hook';
import { consentBody, useReviewCard } from './use-review-card.hook';
import type { WorkspaceLocation } from './workspace-location.util';

/** What the save state says when a proposal opens that is no longer pending. */
export const NOT_PENDING: Record<
  Exclude<PlayProposalStatus, 'pending'>,
  string
> = {
  accepted: 'This proposal was already accepted.',
  discarded: 'This proposal was discarded.',
  superseded: 'A newer proposal replaced this one.',
};

export const STALE_PROPOSAL = 'The play changed after this proposal.';

/** What the save state says while the proposal cannot be read. */
export const LOAD_FAILED = 'Could not load the proposal.';

/**
 * How long a draft handed over to a proposal this canvas does not read (the
 * agent's pending one has not moved to it yet) stays before it goes.
 */
const HANDOVER_WAIT_MS = 10_000;

const NOTHING = () => {};

/**
 * The draft Clu writes in `conversationId` for this agent and play, or the
 * one it handed over to the proposal the URL opens (its card opens the
 * proposal without the play, so on an agent with several plays none is
 * selected while it is read).
 */
function draftFor(
  drafts: Readonly<Record<string, AgentPlayDraft>> | undefined,
  agentId: string,
  conversationId: string | null | undefined,
  playTarget: string,
  proposalId: string | undefined
): AgentPlayDraft | undefined {
  if (!drafts || !conversationId) return undefined;
  return Object.values(drafts).find(
    (draft) =>
      draft.agentId === agentId &&
      draft.conversationId === conversationId &&
      (draft.playTarget === playTarget ||
        (!!proposalId && draft.handedOver === proposalId))
  );
}

/** A 409 that says the play changed after the proposal (not a holds refusal). */
export function isConflict(error: unknown): boolean {
  const response = error as
    | { status?: unknown; data?: { error?: { details?: { code?: unknown } } } }
    | undefined;
  const code = response?.data?.error?.details?.code;
  return (
    response?.status === 409 &&
    !(typeof code === 'string' && code.startsWith('hold'))
  );
}

/**
 * The play proposal the Play tab reviews: `proposal` in the URL, else the
 * agent's pending one. It is read, handed to the builder (which shows it on
 * the canvas with Discard and Accept) and resolved here: Accept saves it as
 * the play and the builder starts again from the saved play; a 409 says the
 * play changed after it and goes back to the saved graph; Discard drops it.
 * A proposal no longer pending shows the saved graph and says so in the save
 * state. `generation` changes whenever the builder must read the play again.
 * Accept and Discard each post a line into the chat Clu proposed in, which is
 * read again when it is the open one. While Clu writes a play in the chat open
 * on this agent, for the play on this canvas, the play as it stands is shown
 * in the proposal's place, read only, until the proposal it becomes is read.
 */
export function usePlayProposal({
  agent,
  location,
  active,
  onNavigate,
  refetchPlay,
  draftDirty = false,
}: {
  agent: AgentDto;
  location: WorkspaceLocation;
  /** The Play tab is open on the play itself (not a run). */
  active: boolean;
  onNavigate: (location: WorkspaceLocation) => void;
  /** Reads the agent's play again; resolves once it is in. */
  refetchPlay: (playId: string) => Promise<unknown>;
  draftDirty?: boolean;
}): {
  proposal: EmbeddedProposal | undefined;
  /** Undefined for a new play, never the first attached play by position. */
  proposalPlayId: string | undefined;
  generation: number;
  writeHeld: boolean;
  holdsDialog: ReactNode;
  reload: {
    busy: boolean;
    error: string;
    discardsDraft: boolean;
    onRetry: () => void;
  } | null;
  /** The proposal could not be read (a dropped connection, a 5xx): it stays
   *  pending and its review link stays, with a way to read it again. */
  load: { busy: boolean; onRetry: () => void } | null;
} {
  const { run, note, fail, saveState } = useAgentEditor();
  const saveStateRef = useRef(saveState);
  saveStateRef.current = saveState;
  const copilot = useCopilotRuntimeOptional();
  const enqueueAcceptedReview = copilot?.enqueueAcceptedPlayReview;
  const { organization, isLoaded } = useOrganization();
  const { unlocked } = useAgentsAccess();
  const authorized =
    isLoaded && organization?.id === agent.organizationId && unlocked;
  // Proposals this page has finished with, so a stale `pendingProposalId`
  // (read again a moment later) does not bring one back.
  const [resolved, setResolved] = useState<ReadonlySet<string>>(
    () => new Set()
  );
  const [generation, setGeneration] = useState(0);
  const [busy, setBusy] = useState(false);
  const [refreshState, setRefreshState] = useState<{
    scope: string;
    revision: number;
    id: string;
    target?: string;
    busy: boolean;
    error: string;
  } | null>(null);
  const draftDirtyRef = useRef(draftDirty);
  draftDirtyRef.current = draftDirty;
  const refreshing = useRef<{ scope: string; revision: number } | null>(null);

  // The agent's pending proposal this page showed. An Accept or Discard made
  // elsewhere (the chat card) clears `pendingProposalId` before the proposal
  // reads again, so keep reading it until its new status is handled here.
  const agentKey = `${agent.organizationId}:${agent.id}`;
  const [shownPending, setShownPending] = useState<{
    key: string;
    id: string;
  } | null>(null);
  const candidate =
    location.proposal ??
    agent.pendingProposalId ??
    (shownPending?.key === agentKey ? shownPending.id : undefined);
  const proposalId =
    authorized && active && candidate && !resolved.has(candidate)
      ? candidate
      : undefined;

  const {
    currentData: data,
    isError,
    isFetching,
    error: readError,
    refetch: readAgain,
  } = useGetPlayProposalQuery(
    {
      agentId: agent.id,
      proposalId: proposalId ?? '',
      organizationId: agent.organizationId,
    },
    { skip: !proposalId }
  );
  const [accept] = useAcceptPlayProposalMutation();
  const [discard] = useDiscardPlayProposalMutation();
  const refreshChat = useRefreshAgentChat();
  const selected = selectedAgentPlay(agent, location.play);
  // The play Clu is writing in the chat open on this agent, for the play on
  // this canvas ("new" on a new play's), drawn as it stands.
  const dispatch = useDispatch();
  const drafts = useSelector(selectAgentPlayDrafts);
  const draft =
    authorized && active && copilot?.agentSession?.agentId === agent.id
      ? draftFor(
          drafts,
          agent.id,
          copilot.conversationId,
          selected ?? 'new',
          location.proposal
        )
      : undefined;
  const target =
    data?.playbookId === null
      ? undefined
      : selectedAgentPlay(agent, data?.playbookId);
  // Missing target is legacy-compatible only for a sole play or a first new play.
  const targetValid =
    data?.playbookId === null ||
    Boolean(target) ||
    (data?.playbookId === undefined && agent.playIds.length === 0);
  const current =
    data &&
    data.id === proposalId &&
    targetValid &&
    (location.proposal || !selected || !target || selected === target)
      ? data
      : undefined;
  useEffect(() => {
    if (!proposalId || location.proposal || current?.status !== 'pending')
      return;
    setShownPending((previous) =>
      previous?.key === agentKey && previous.id === proposalId
        ? previous
        : { key: agentKey, id: proposalId }
    );
  }, [proposalId, location.proposal, current?.status, agentKey]);
  const scope = `${agent.organizationId}:${agent.id}:${location.play ?? ''}:${location.proposal ?? ''}`;
  const allowed = authorized && active;
  const live = useRef({ scope, allowed, candidate, revision: 0 });
  const revision =
    live.current.revision +
    Number(live.current.scope !== scope || live.current.allowed !== allowed);
  live.current = { scope, allowed, candidate, revision };
  const mounted = useRef(true);
  const pending = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const stillCurrent = useCallback(
    () =>
      mounted.current &&
      live.current.scope === scope &&
      live.current.revision === revision &&
      live.current.allowed &&
      (!live.current.candidate || live.current.candidate === proposalId),
    [scope, revision, proposalId]
  );

  const resolve = useCallback(
    (id: string, clearUrl: boolean, playId = target) => {
      setResolved((previous) => new Set(previous).add(id));
      if (clearUrl)
        onNavigate({ tab: 'play', ...(playId ? { play: playId } : {}) });
    },
    [target, onNavigate]
  );

  // Opened after it was resolved elsewhere, or not there (a 404): the saved
  // graph. A read that failed in transit (CLE-565: the browser's connection
  // dropped, or the API was restarting) leaves the proposal pending, with its
  // review link, until a read gets through.
  const [unreadable, setUnreadable] = useState<string | null>(null);
  useEffect(() => {
    if (!proposalId) return;
    if (
      current &&
      current.status !== 'pending' &&
      current.status !== 'accepted'
    ) {
      note(NOT_PENDING[current.status]);
      resolve(proposalId, true);
    } else if (isError && !isFetching) {
      fail(LOAD_FAILED);
      if (isMissingRecord(readError)) resolve(proposalId, true);
      else setUnreadable(proposalId);
    } else if (current && unreadable === proposalId) {
      setUnreadable(null);
      const shown = saveStateRef.current;
      if (shown.status === 'failed' && shown.message === LOAD_FAILED)
        note('Proposal ready to review.');
    }
  }, [
    proposalId,
    current,
    isError,
    isFetching,
    readError,
    unreadable,
    note,
    fail,
    resolve,
  ]);

  // A draft the turn handed over to its proposal stays until that proposal
  // is the one read here, so the canvas never shows the saved play between
  // them; then it goes, and the turn's progress event learns when.
  const handedOver = draft?.handedOver;
  const landed = Boolean(handedOver) && current?.id === handedOver;
  const draftKey = draft?.key;
  const handedOverAt = draft?.handedOverAt ?? 0;
  useEffect(() => {
    if (!draftKey || !handedOver) return;
    const clear = () => dispatch(clearAgentPlayDraft(draftKey));
    if (landed) {
      // Held back by the user's unsaved edits, it is not on the canvas yet.
      if (!draftDirtyRef.current) proposalRendered(draftKey);
      clear();
      return;
    }
    // It will not show here: gone, unreadable, or not the one read here.
    if (
      resolved.has(handedOver) ||
      (proposalId === handedOver && isError && !isFetching)
    ) {
      clear();
      return;
    }
    // Still being read here; a read that came back but is not shown (another
    // play's) waits like one read elsewhere.
    if (proposalId === handedOver && (isFetching || data?.id !== handedOver))
      return;
    const timer = setTimeout(
      clear,
      Math.max(0, handedOverAt + HANDOVER_WAIT_MS - Date.now())
    );
    return () => clearTimeout(timer);
  }, [
    draftKey,
    handedOver,
    handedOverAt,
    landed,
    resolved,
    proposalId,
    isError,
    isFetching,
    data?.id,
    dispatch,
  ]);

  const refetch = useRef(refetchPlay);
  refetch.current = refetchPlay;
  const visibleRefresh =
    refreshState?.scope === scope &&
    refreshState.revision === revision &&
    (!proposalId || refreshState.id === proposalId)
      ? refreshState
      : null;
  const remoteAccepted =
    current?.status === 'accepted' && !resolved.has(current.id);
  // This is derived during render, so the old canvas cannot autosave in the
  // interval before an effect notices that the proposal was accepted elsewhere.
  const reloadNeeded = Boolean(visibleRefresh || remoteAccepted);
  const reloadSaved = useCallback(
    async (
      id: string,
      savedTarget: string | undefined,
      discardDraft = false
    ) => {
      if (
        !stillCurrent() ||
        (refreshing.current?.scope === scope &&
          refreshing.current.revision === revision)
      )
        return;
      const request = { scope, revision };
      refreshing.current = request;
      setRefreshState({
        scope,
        revision,
        id,
        target: savedTarget,
        busy: true,
        error: '',
      });
      try {
        if (savedTarget) {
          await pendingPlaySave(`${agent.organizationId}:${savedTarget}`);
          if (!stillCurrent()) return;
          await refetch.current(savedTarget);
        }
        if (!stillCurrent()) return;
        if (draftDirtyRef.current && !discardDraft) {
          setRefreshState({
            scope,
            revision,
            id,
            target: savedTarget,
            busy: false,
            error: '',
          });
          return;
        }
        resolve(id, true, savedTarget);
        setGeneration((value) => value + 1);
        setRefreshState(null);
      } catch (error) {
        if (stillCurrent())
          setRefreshState({
            scope,
            revision,
            id,
            target: savedTarget,
            busy: false,
            error: serverReason(
              error,
              'Could not load the latest saved play. Your canvas is held until it loads.'
            ),
          });
      } finally {
        if (refreshing.current === request) refreshing.current = null;
      }
    },
    [stillCurrent, scope, revision, agent.organizationId, resolve]
  );
  const reloadId = visibleRefresh?.id ?? proposalId;
  const reloadTarget = visibleRefresh ? visibleRefresh.target : target;
  const reloadBusy = Boolean(visibleRefresh?.busy);
  const reloadError = visibleRefresh?.error ?? '';
  useEffect(() => {
    if (
      !reloadNeeded ||
      !reloadId ||
      busy ||
      draftDirty ||
      reloadBusy ||
      reloadError
    )
      return;
    void reloadSaved(reloadId, reloadTarget);
  }, [
    reloadNeeded,
    reloadId,
    reloadTarget,
    reloadSaved,
    busy,
    draftDirty,
    reloadBusy,
    reloadError,
  ]);

  // The chat Clu proposed in, which Accept and Discard post a line to.
  const chatId = current?.conversationId ?? null;

  // Held records name a kept step as the API names a removed one
  // (`step_headline`), so a step reads the same either way.
  const proposedGraph = current?.graph as CustomGraph | undefined;
  const proposedTrigger = graphTrigger(proposedGraph);
  const heldAccept = useHeldRecordsDecision(
    scope,
    active && authorized,
    Object.fromEntries([
      ...(proposedGraph?.nodes ?? []).map((node) => [
        node.id,
        stepHeadline(node, proposedTrigger),
      ]),
      ...(current?.removed ?? []).map((node) => [node.id, node.headline]),
    ])
  );
  const runHeldAccept = heldAccept.run;
  const affectedHolds = current?.holds_affected;
  const blocked = current ? proposalIsBlocked(current) : false;
  const review = useReviewCard(current);
  const reviewCard = review.card;
  const canAcceptRef = useRef(false);
  canAcceptRef.current =
    current?.status === 'pending' && !blocked && !review.held;
  const onAccept = useCallback(async () => {
    if (
      !proposalId ||
      current?.status !== 'pending' ||
      !canAcceptRef.current ||
      pending.current ||
      !stillCurrent()
    )
      return;
    pending.current = true;
    setBusy(true);
    let conflict = false;
    let cancelled = false;
    let saved: AgentDto | undefined;
    const ok = await run(
      () =>
        runHeldAccept(async (holds) => {
          if (!stillCurrent() || !canAcceptRef.current)
            throw new HoldDecisionCancelled();
          saved = await accept({
            agentId: agent.id,
            proposalId,
            organizationId: agent.organizationId,
            ...holdsBody(holds),
            ...consentBody(reviewCard),
          }).unwrap();
          // Accept succeeded even if the graph refresh or page later goes away.
          // The provider routes only to this original org/user/Agent/chat host.
          if (chatId)
            enqueueAcceptedReview?.({
              organizationId: agent.organizationId,
              agentId: agent.id,
              conversationId: chatId,
              proposalId,
              playId:
                target ?? (saved && addedAgentPlay(agent, saved)) ?? undefined,
            });
          return saved;
        }, affectedHolds),
      (error) => {
        if (error instanceof HoldDecisionCancelled) {
          cancelled = true;
          return '';
        }
        conflict = isConflict(error);
        return conflict
          ? STALE_PROPOSAL
          : serverReason(error, 'Could not accept the proposal.');
      }
    );
    if (!stillCurrent()) {
      setBusy(false);
      pending.current = false;
      return;
    }
    if (cancelled) note('Changes were not accepted.');
    if (ok || conflict) {
      // Keep the old canvas held if the authoritative read fails. A cached
      // graph must never be remounted as if it were the accepted version.
      const savedTarget = target ?? (saved && addedAgentPlay(agent, saved));
      await reloadSaved(proposalId, savedTarget);
    }
    if (ok && !enqueueAcceptedReview) refreshChat(chatId);
    setBusy(false);
    pending.current = false;
  }, [
    proposalId,
    run,
    runHeldAccept,
    affectedHolds,
    note,
    accept,
    reloadSaved,
    refreshChat,
    enqueueAcceptedReview,
    chatId,
    agent,
    target,
    stillCurrent,
    current?.status,
    reviewCard,
  ]);

  const onDiscard = useCallback(async () => {
    if (
      !proposalId ||
      current?.status !== 'pending' ||
      pending.current ||
      !stillCurrent()
    )
      return;
    pending.current = true;
    setBusy(true);
    const ok = await run(
      () =>
        discard({
          agentId: agent.id,
          proposalId,
          organizationId: agent.organizationId,
        }).unwrap(),
      (error) => serverReason(error, 'Could not discard the proposal.')
    );
    if (!stillCurrent()) {
      setBusy(false);
      pending.current = false;
      return;
    }
    if (ok) {
      resolve(proposalId, true);
      refreshChat(chatId);
    }
    setBusy(false);
    pending.current = false;
  }, [
    proposalId,
    run,
    discard,
    agent.id,
    agent.organizationId,
    resolve,
    refreshChat,
    chatId,
    stillCurrent,
    current?.status,
  ]);

  const proposal = useMemo<EmbeddedProposal | undefined>(() => {
    if (!proposalId || !current || current.status !== 'pending' || reloadNeeded)
      return undefined;
    return {
      id: current.id,
      graph: current.graph as unknown as CustomGraph,
      preview: current.preview,
      defaultedLimits: current.defaultedLimits,
      testable: current.testable,
      conversationId: current.conversationId ?? null,
      added: current.added,
      changed: current.changed,
      removed: current.removed.map((step) => step.headline),
      summary: current.summary,
      buildState: current.buildState,
      canAccept: !blocked,
      reviewCard,
      requirementRevision: current.requirementRevision,
      requirements: current.requirements,
      blockedReasons: current.blockedReasons,
      issues: current.issues.map((issue) => ({
        nodeId: issue.nodeId ?? undefined,
        code: issue.code,
        message: issue.message,
        severity: issue.severity,
        blocks: issue.blocks,
      })),
      mappingWarnings: current.mappingWarnings ?? [],
      turnsOn: abilitiesTurnedOn(current.agentChanges),
      // A card the flag doesn't show yet (still loading) holds Accept.
      busy: busy || review.held,
      onAccept: () => void onAccept(),
      onDiscard: () => void onDiscard(),
    };
  }, [
    proposalId,
    current,
    blocked,
    review.held,
    reviewCard,
    busy,
    onAccept,
    onDiscard,
    reloadNeeded,
  ]);

  // The draft on the canvas in the proposal's place, read only.
  const drafted = useMemo<EmbeddedProposal | undefined>(
    () =>
      draft?.graph && !landed
        ? {
            graph: draft.graph,
            added: draft.added,
            changed: draft.changed,
            removed: [],
            summary: draft.label,
            issues: [],
            busy: true,
            drafting: true,
            onAccept: NOTHING,
            onDiscard: NOTHING,
          }
        : undefined,
    [draft, landed]
  );
  const draftPlay = selectedAgentPlay(agent, draft?.playTarget);

  return {
    proposal: drafted ?? proposal,
    // A draft is for the play it changes, as its proposal will be.
    proposalPlayId: drafted ? draftPlay : target,
    generation,
    writeHeld: busy || reloadNeeded,
    holdsDialog: heldAccept.dialog,
    reload: reloadNeeded
      ? {
          busy: reloadBusy || busy,
          error: reloadError,
          discardsDraft: draftDirty,
          onRetry: () => {
            if (reloadId) void reloadSaved(reloadId, reloadTarget, true);
          },
        }
      : null,
    load:
      proposalId && unreadable === proposalId && !current
        ? { busy: isFetching, onRetry: () => void readAgain() }
        : null,
  };
}
